'use client';

import { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Menu, X, User, LogOut } from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import { Link } from '@/lib/i18n/navigation';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { collections } from '@/lib/catalog/collections';
import { LocaleSwitcher } from '@/components/layout/locale-switcher';
import { cn } from '@/lib/utils';
import type { Locale } from '@/lib/i18n/routing';

interface NavItem {
  href: string;
  label: string;
  badge?: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

export function SideNav() {
  const t = useTranslations('nav');
  const tCommon = useTranslations('common');
  const tHeader = useTranslations('header');
  const locale = useLocale() as Locale;
  const isEn = locale === 'en-US';

  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const user = session?.user;
  const close = (): void => setOpen(false);

  const groups: NavGroup[] = [
    {
      title: t('collections'),
      items: [
        { href: '/colecciones', label: isEn ? 'The 9 collections' : 'Las 9 colecciones' },
        ...collections.map((c) => ({
          href: `/colecciones/${c.slug}`,
          label: isEn ? c.nameEn : c.nameEs,
        })),
      ],
    },
    {
      title: t('ritual'),
      items: [
        { href: '/manifiesto', label: t('manifesto'), badge: isEn ? 'new' : 'nuevo' },
        {
          href: '/novela',
          label: isEn ? 'Novela (preview)' : 'Novela (propuesta)',
          badge: isEn ? 'preview' : 'propuesta',
        },
        { href: '/tarot', label: t('tarotDailyCard') },
      ],
    },
    {
      title: t('editorial'),
      items: [{ href: '/editorial', label: isEn ? 'Essays & lookbooks' : 'Ensayos y lookbooks' }],
    },
    {
      title: t('help'),
      items: [
        { href: '/ayuda', label: t('assistant') },
        { href: '/buscar', label: t('searchCatalog') },
        {
          href: '/legal/envios',
          label: isEn ? 'Shipping · returns' : 'Envíos · devoluciones',
        },
        {
          href: '/legal/metodos-de-pago',
          label: isEn ? 'Payment methods' : 'Métodos de pago',
        },
      ],
    },
  ];

  const regionLine = isEn ? 'MX · English · MXN' : 'MX · español · MXN';

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={tCommon('openMenu')}
          className="flex h-10 w-10 items-center justify-center rounded-sm hover:bg-[var(--color-surface)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
        >
          <Menu className="h-5 w-5" strokeWidth={1.25} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        className={cn(
          'w-[90vw] max-w-[360px] border-r border-[var(--color-border)] bg-[var(--color-bg)] p-0',
          'sm:w-[360px]'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Header bar */}
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-6 py-5">
            <span className="type-eyebrow text-[var(--color-fg-subtle)]">NQORO · Φ</span>
            <button
              type="button"
              onClick={close}
              aria-label={tCommon('closeMenu')}
              className="flex h-8 w-8 items-center justify-center rounded-sm hover:bg-[var(--color-surface)]"
            >
              <X className="h-4 w-4" strokeWidth={1.25} />
            </button>
          </div>

          {/* Nav groups */}
          <nav className="flex-1 overflow-y-auto px-6 py-8">
            {groups.map((group, i) => (
              <div key={group.title} className={i > 0 ? 'mt-9' : ''}>
                <p className="type-eyebrow mb-3 text-[var(--color-fg-subtle)]">{group.title}</p>
                <ul className="flex flex-col gap-1.5">
                  {group.items.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        onClick={close}
                        className="type-nav inline-flex items-baseline gap-2 py-1 text-[var(--color-fg)] transition-colors hover:text-[var(--color-accent)]"
                      >
                        {item.label}
                        {item.badge ? (
                          <span className="type-caption text-[var(--color-accent)] italic">
                            {item.badge}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Mi cuenta — condicional */}
            <div className="mt-9">
              <p className="type-eyebrow mb-3 text-[var(--color-fg-subtle)]">
                {tHeader('myAccount')}
              </p>
              {user ? (
                <ul className="flex flex-col gap-1.5">
                  <li className="mb-2 border-b border-[var(--color-border)] pb-3">
                    <p className="type-caption text-[var(--color-fg-subtle)]">
                      {tHeader('sessionAs')}
                    </p>
                    <p className="type-body-sm truncate text-[var(--color-fg)]">{user.email}</p>
                  </li>
                  <AccountLink href="/cuenta" label={tHeader('myAccount')} onClick={close} />
                  <AccountLink href="/cuenta/pedidos" label={tHeader('myOrders')} onClick={close} />
                  <AccountLink
                    href="/cuenta/wishlist"
                    label={tHeader('myWishlist')}
                    onClick={close}
                  />
                  <AccountLink
                    href="/cuenta/facturas"
                    label={isEn ? 'CFDI invoices' : 'Facturas CFDI'}
                    onClick={close}
                  />
                  <AccountLink
                    href="/cuenta/tarot"
                    label={isEn ? 'Tarot — history' : 'Tarot — historial'}
                    onClick={close}
                  />
                  <AccountLink
                    href="/cuenta/privacidad"
                    label={isEn ? 'Privacy ARCO' : 'Privacidad ARCO'}
                    onClick={close}
                  />
                  <li className="mt-2 border-t border-[var(--color-border)] pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        void signOut({ callbackUrl: '/' });
                      }}
                      className="type-nav flex items-center gap-2 py-1 text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-danger)]"
                    >
                      <LogOut className="h-4 w-4" strokeWidth={1.25} />
                      {t('logout')}
                    </button>
                  </li>
                </ul>
              ) : (
                <Link
                  href="/login"
                  onClick={close}
                  className="type-nav inline-flex items-center gap-2 py-1 text-[var(--color-fg)] transition-colors hover:text-[var(--color-accent)]"
                >
                  <User className="h-4 w-4" strokeWidth={1.25} />
                  {t('login')}
                </Link>
              )}
            </div>
          </nav>

          {/* Footer bar */}
          <div className="border-t border-[var(--color-border)] px-6 py-5">
            <a
              href="mailto:contacto@nqoro.com"
              onClick={close}
              className="type-nav block py-1 text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)]"
            >
              {t('contact')}
            </a>
            <div className="mt-3 flex gap-4">
              <a
                href="https://instagram.com/nqoro"
                target="_blank"
                rel="noreferrer"
                onClick={close}
                className="type-caption text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)]"
              >
                Instagram
              </a>
              <a
                href="https://tiktok.com/@nqoro"
                target="_blank"
                rel="noreferrer"
                onClick={close}
                className="type-caption text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)]"
              >
                TikTok
              </a>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="type-caption text-[var(--color-fg-subtle)]">{regionLine}</p>
              <LocaleSwitcher />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AccountLink({
  href,
  label,
  onClick,
}: {
  href: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <li>
      <Link
        href={href}
        onClick={onClick}
        className="type-nav block py-1 text-[var(--color-fg)] transition-colors hover:text-[var(--color-accent)]"
      >
        {label}
      </Link>
    </li>
  );
}
